export interface HighlightSegment {
  text: string;
  isMatch: boolean;
}

export interface HighlightMatchParams {
  text: string;
  query: string;
}

interface FoldedText {
  folded: string;
  originalIndexByFoldedIndex: number[];
}

interface FindFoldedMatchesParams {
  folded: string;
  foldedQuery: string;
}

interface BuildSegmentsParams {
  text: string;
  foldedMatches: Array<[number, number]>;
  originalIndexByFoldedIndex: number[];
}

const DIACRITICS_REGEX = /[̀-ͯ]/g;

const noHighlight = (text: string): HighlightSegment[] => [{ text, isMatch: false }];

const foldText = (text: string): FoldedText => {
  let folded = '';
  const originalIndexByFoldedIndex: number[] = [];

  for (let index = 0; index < text.length; index += 1) {
    const foldedChar = text[index].normalize('NFD').replace(DIACRITICS_REGEX, '').toLowerCase();

    for (const char of foldedChar) {
      folded += char;
      originalIndexByFoldedIndex.push(index);
    }
  }

  return { folded, originalIndexByFoldedIndex };
};

const findFoldedMatches = ({
  folded,
  foldedQuery,
}: FindFoldedMatchesParams): Array<[number, number]> => {
  const matches: Array<[number, number]> = [];
  let matchIndex = folded.indexOf(foldedQuery);

  while (matchIndex !== -1) {
    matches.push([matchIndex, matchIndex + foldedQuery.length]);
    matchIndex = folded.indexOf(foldedQuery, matchIndex + foldedQuery.length);
  }

  return matches;
};

const buildSegments = ({
  text,
  foldedMatches,
  originalIndexByFoldedIndex,
}: BuildSegmentsParams): HighlightSegment[] => {
  const segments: HighlightSegment[] = [];
  let cursor = 0;

  for (const [foldedStart, foldedEnd] of foldedMatches) {
    const originalStart = originalIndexByFoldedIndex[foldedStart];
    const originalEnd = originalIndexByFoldedIndex[foldedEnd] ?? text.length;

    if (originalStart > cursor) {
      segments.push({ text: text.slice(cursor, originalStart), isMatch: false });
    }

    segments.push({ text: text.slice(originalStart, originalEnd), isMatch: true });
    cursor = originalEnd;
  }

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), isMatch: false });
  }

  return segments;
};

export const highlightMatch = ({ text, query }: HighlightMatchParams): HighlightSegment[] => {
  const trimmedQuery = query?.trim() ?? '';

  if (!text || !trimmedQuery) {
    return noHighlight(text ?? '');
  }

  const { folded, originalIndexByFoldedIndex } = foldText(text);
  const { folded: foldedQuery } = foldText(trimmedQuery);

  if (!foldedQuery) {
    return noHighlight(text);
  }

  const foldedMatches = findFoldedMatches({ folded, foldedQuery });

  if (foldedMatches.length === 0) {
    return noHighlight(text);
  }

  return buildSegments({ text, foldedMatches, originalIndexByFoldedIndex });
};
